import { useEffect, useState } from "react";
import { Center, Group, SegmentedControl, Stack, Text, Title } from "@mantine/core";
import { useParams } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { ChartWrapper } from "../components/ChartWrapper";
import { LogoImg } from "../components/LogoImg";
import { formatPrice } from "../utils/price";
import { getRetailerColor } from "../utils/retailer-colors";
import { api } from "../lib/api";

interface DataPoint {
  timestamp: string;
  avgPrice: string;
  minPrice: string;
  maxPrice: string;
  observationCount: number;
}

interface Series {
  retailerId: string;
  retailerName: string;
  retailerDomain: string | null;
  dataPoints: DataPoint[];
}

interface HistoryResponse {
  productId: string;
  productName: string;
  series: Series[];
}

const DATE_RANGES: Record<string, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
  "1y": 365,
};

export function HistoryPage() {
  const { productId } = useParams<{ productId: string }>();
  const [data, setData] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("30d");

  useEffect(() => {
    if (!productId) return;

    let cancelled = false;
    const controller = new AbortController();

    const days = DATE_RANGES[range] ?? 30;
    const endDate = new Date().toISOString().slice(0, 10);
    const startDate = new Date(Date.now() - days * 86_400_000)
      .toISOString()
      .slice(0, 10);

    api
      .get<HistoryResponse>(`/history/${productId}`, {
        params: { start_date: startDate, end_date: endDate, bucket: "1 day" },
        signal: controller.signal,
      })
      .then(({ data: resp }) => {
        if (!cancelled) setData(resp);
      })
      .catch((err) => {
        if (cancelled) return;
        notifications.show({
          title: "Failed to load history",
          message: err instanceof Error ? err.message : "Network error",
          color: "red",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [productId, range]);

  if (!productId) {
    return (
      <Stack gap="md">
        <Title order={2}>Price History</Title>
        <Center py={60}>
          <Text c="dimmed">
            Select a product from the Feed or Grid to view its price history.
          </Text>
        </Center>
      </Stack>
    );
  }

  const isEmpty = !data || data.series.length === 0;

  const chartOption = data
    ? {
        tooltip: {
          trigger: "axis" as const,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: (params: any) => {
            const arr = Array.isArray(params) ? params : [params];
            if (!arr.length) return "";
            const date = new Date(arr[0].value[0]).toLocaleDateString(
              "en-US",
              { month: "short", day: "numeric", year: "numeric" },
            );
            const lines = arr.map(
              (p: { color: string; seriesName: string; value: [string, number] }) =>
                `<span style="color:${p.color}">\u25CF</span> ${p.seriesName}: ${formatPrice(p.value[1])}`,
            );
            return `<b>${date}</b><br/>${lines.join("<br/>")}`;
          },
        },
        legend: { bottom: 0, type: "scroll" as const },
        grid: { left: 80, right: 20, top: 20, bottom: 60 },
        xAxis: { type: "time" as const },
        yAxis: {
          type: "value" as const,
          axisLabel: { formatter: (val: number) => formatPrice(val) },
        },
        series: data.series.map((s) => ({
          name: s.retailerName,
          type: "line" as const,
          smooth: true,
          symbol: "circle",
          symbolSize: 4,
          color: getRetailerColor(s.retailerName),
          data: s.dataPoints.map((dp) => [dp.timestamp, parseFloat(dp.avgPrice)]),
        })),
      }
    : {};

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={2}>{data?.productName ?? "Price History"}</Title>
          {data && (
            <Text size="sm" c="dimmed">
              Price history across {data.series.length} retailers
            </Text>
          )}
        </div>
        <SegmentedControl
          value={range}
          onChange={setRange}
          data={Object.keys(DATE_RANGES)}
          size="sm"
        />
      </Group>

      <ChartWrapper
        option={chartOption}
        loading={loading}
        empty={isEmpty}
        ariaLabel={`Price history chart for ${data?.productName ?? "product"}`}
      />

      {/* Retailer legend with current prices */}
      {data && (
        <Group gap="lg">
          {data.series.map((s) => {
            const lastPoint = s.dataPoints[s.dataPoints.length - 1];
            return (
              <Group key={s.retailerId} gap="xs">
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 2,
                    backgroundColor: getRetailerColor(s.retailerName),
                  }}
                />
                {s.retailerDomain && (
                  <LogoImg
                    domain={s.retailerDomain}
                    size={16}
                    alt={s.retailerName}
                  />
                )}
                <Text size="sm">{s.retailerName}</Text>
                {lastPoint && (
                  <Text size="sm" fw={600}>
                    {formatPrice(parseFloat(lastPoint.avgPrice))}
                  </Text>
                )}
              </Group>
            );
          })}
        </Group>
      )}
    </Stack>
  );
}
